import { Box, Card } from '@mui/material';
import type { SxProps, Theme } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import { BORDER, TEXT_MUTED } from 'Components/FilterDropdownTrigger';
import useTranslation from 'Hooks/useTranslation';
import MultiSelectFilter, { type MultiSelectOptionI } from './MultiSelectFilter';

export interface JobTitleCoursesFilterValues {
  search: string;
  jobTitleIds: string[];
  categoryIds: string[];
}

interface Props {
  values: JobTitleCoursesFilterValues;
  onChange: (next: JobTitleCoursesFilterValues) => void;
  jobTitleOptions: MultiSelectOptionI[];
  categoryOptions: MultiSelectOptionI[];
}

const TEXT_STRONG = '#1F2A3A';
const SEARCH_DEBOUNCE_MS = 350;

type IconProps = { size?: number; color?: string };

const IconSearch = ({ size = 15, color = TEXT_MUTED }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.5-4.5" />
  </svg>
);

const IconJobTitle = ({ size = 14, color = TEXT_MUTED }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconCategory = ({ size = 14, color = TEXT_MUTED }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M3 7h18M3 12h18M3 17h18" />
  </svg>
);

const styles = {
  card: {
    p: 2,
    mb: 2,
    borderRadius: '12px',
    border: `1px solid ${BORDER}`,
    boxShadow: 'none',
    bgcolor: '#fff',
  } as SxProps<Theme>,
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    flexWrap: 'wrap',
  } as SxProps<Theme>,
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: '9px',
    border: `1px solid ${BORDER}`,
    borderRadius: '8px',
    flex: '1 1 260px',
    maxWidth: 320,
    bgcolor: '#fff',
  } as SxProps<Theme>,
  searchInput: {
    border: 'none',
    outline: 'none',
    fontSize: 13,
    flex: 1,
    color: TEXT_STRONG,
    background: 'transparent',
    width: '100%',
    fontFamily: 'inherit',
  } as const,
  clearBtn: {
    background: 'none',
    border: 'none',
    color: TEXT_MUTED,
    fontSize: 12.5,
    cursor: 'pointer',
    textDecoration: 'underline',
    p: 0,
    fontFamily: 'inherit',
  } as const,
};

/**
 * Barra de filtros del Reporte de Cursos por Puesto.
 *
 * Componente controlado: el estado vive en `useJobTitleCoursesReportPage`, que
 * es también quien resetea la paginación cuando un filtro cambia.
 *
 * El buscador de curso se debouncea; los multiselect emiten de inmediato porque
 * un clic ya es una intención explícita del usuario.
 */
export default function JobTitleCoursesFilters({ values, onChange, jobTitleOptions, categoryOptions }: Props) {
  const { t2 } = useTranslation('JobTitleCoursesReport');
  const [searchDraft, setSearchDraft] = useState(values.search);

  // Mantiene el input sincronizado cuando el reset viene de fuera ("Limpiar").
  useEffect(() => {
    setSearchDraft(values.search);
  }, [values.search]);

  const emittedSearchRef = useRef(values.search);
  useEffect(() => {
    if (emittedSearchRef.current === searchDraft) return;
    const timer = setTimeout(() => {
      emittedSearchRef.current = searchDraft;
      onChange({ ...values, search: searchDraft });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft, values, onChange]);

  const handleClear = () => {
    emittedSearchRef.current = '';
    setSearchDraft('');
    onChange({ search: '', jobTitleIds: [], categoryIds: [] });
  };

  const hasFilters = !!values.search || values.jobTitleIds.length > 0 || values.categoryIds.length > 0;

  return (
    <Card sx={styles.card} elevation={0}>
      <Box sx={styles.row}>
        <Box sx={styles.searchBox}>
          <IconSearch />
          <input
            value={searchDraft}
            placeholder={t2('filters.searchPlaceholder') as string}
            style={styles.searchInput}
            onChange={event => setSearchDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                emittedSearchRef.current = searchDraft;
                onChange({ ...values, search: searchDraft });
              }
            }}
          />
        </Box>

        <MultiSelectFilter
          icon={<IconJobTitle />}
          label={t2('filters.jobTitles') as string}
          options={jobTitleOptions}
          value={values.jobTitleIds}
          onChange={next => onChange({ ...values, jobTitleIds: next })}
          allLabel={t2('filters.allJobTitles') as string}
          searchPlaceholder={t2('filters.searchJobTitle') as string}
          emptyLabel={t2('filters.noMatches') as string}
          selectedNoun={t2('filters.jobTitlesNoun') as string}
        />

        <MultiSelectFilter
          icon={<IconCategory />}
          label={t2('filters.categories') as string}
          options={categoryOptions}
          value={values.categoryIds}
          onChange={next => onChange({ ...values, categoryIds: next })}
          allLabel={t2('filters.allCategories') as string}
          emptyLabel={t2('filters.noMatches') as string}
          selectedNoun={t2('filters.categoriesNoun') as string}
        />

        <Box sx={{ flex: '1 1 0%' }} />

        {hasFilters && (
          <Box component="button" type="button" onClick={handleClear} sx={styles.clearBtn}>
            {t2('filters.clear')}
          </Box>
        )}
      </Box>
    </Card>
  );
}
